import { useToast } from '@app/core/notifications';
import { useAssetsUpload } from '@mi-front/api-v2';
import { FileInputProps, Spinner } from '@mi-front/ui';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Box, Button, Chip, Stack, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

const HiddenFileInput = styled('input')({
  display: 'none',
});

export interface Props {
  inputProps?: Partial<FileInputProps>;
  onUploaded: (url: string) => void;
}

export function UploadAssetMui(p: Props) {
  const { formatMessage } = useIntl();
  const upload = useAssetsUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { notifyError } = useToast();

  function doUpload(file: File) {
    upload.mutate(
      {
        data: {
          file,
        },
      },
      {
        onSuccess: (data) => {
          p.onUploaded(data.url);
        },
        onError: (error) => {
          notifyError(
            formatMessage({
              id: 'assets.upload.error',
              defaultMessage: 'Ocurrió un error al cargar el archivo, intente nuevamente',
            }),
          );

          console.error(error);
        },
      },
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    const maxSizeMb = p.inputProps?.maxSizeMb;

    if (maxSizeMb && files.length && files.find((f: File) => f.size > maxSizeMb * 1024 * 1024)) {
      notifyError(
        formatMessage(
          {
            id: 'assets.upload.errorFileSizeExceeded',
            defaultMessage: 'Los archivos no deben superar {maxSizeMb}MB',
          },
          { maxSizeMb },
        ),
      );
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      return;
    }

    if (files[0]) {
      doUpload(files[0]);
    }

    // Reset input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTriggerClick = () => {
    fileInputRef.current?.click();
  };

  const displayMode = p.inputProps?.displayMode ?? 'button';
  const maxSizeMb = p.inputProps?.maxSizeMb;
  const helperText = p.inputProps?.helperText;
  const triggerSize = p.inputProps?.size === 'sm' ? 'small' : 'medium';
  const isPending = upload.isPending;
  const triggerLabel =
    p.inputProps?.label ||
    formatMessage({
      id: 'assets.upload.label',
      defaultMessage: 'Subir recurso',
    });
  const chipIcon = isPending ? (
    <Spinner />
  ) : React.isValidElement(p.inputProps?.startIcon) ? (
    p.inputProps.startIcon
  ) : (
    <CloudUploadIcon />
  );
  const showUploadHelp = Boolean(maxSizeMb || helperText);
  const uploadHelpTooltip = showUploadHelp ? (
    <Stack spacing={0.5}>
      {maxSizeMb != null && (
        <Box>
          {formatMessage(
            {
              id: 'assets.upload.maxSize',
              defaultMessage: 'Máx. {max}MB.',
            },
            { max: maxSizeMb },
          )}
        </Box>
      )}
      {helperText && <Box>{helperText}</Box>}
    </Stack>
  ) : null;

  const uploadTrigger =
    displayMode === 'chip' ? (
      <Chip
        variant="outlined"
        size={triggerSize}
        disabled={isPending}
        clickable={!isPending}
        icon={chipIcon}
        label={triggerLabel}
        onClick={handleTriggerClick}
      />
    ) : (
      <Button
        variant="outlined"
        color="primary"
        size={triggerSize}
        disabled={isPending}
        startIcon={isPending ? <Spinner /> : (p.inputProps?.startIcon ?? <CloudUploadIcon />)}
        onClick={handleTriggerClick}
      >
        {triggerLabel}
      </Button>
    );

  return (
    <Box>
      {showUploadHelp ? (
        <Tooltip placement="top" title={uploadHelpTooltip}>
          {uploadTrigger}
        </Tooltip>
      ) : (
        uploadTrigger
      )}
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        multiple={false}
        accept={p.inputProps?.accept}
        onChange={handleFileChange}
        disabled={upload.isPending}
      />
    </Box>
  );
}
